import { Link } from "@tanstack/react-router";
import { ArrowRight, LogIn } from "lucide-react";
import { motion, type Variants } from "motion/react";

import { m } from "@/paraglide/messages.js";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const glowVariants: Variants = {
  hidden: { opacity: 0, scale: 0.94 },
  show: { opacity: 1, scale: 1, transition: { duration: 1.2, ease: EASE } },
};

const contentVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.1 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const ctaButtonsVariants: Variants = {
  hidden: { opacity: 0, y: 18, scale: 0.96 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: EASE } },
};

const viewport = { once: true, margin: "-80px" } as const;

/**
 * Closing call to action. A glowing primary band rises behind the headline so
 * it reads as the page's conversion point. The glow is an SVG built from
 * `var(--primary)` and `var(--background)`, so it stays on-theme with no
 * asset pipeline. The glow blooms in and the headline, sub, and buttons
 * stagger up as the section scrolls into view.
 */
export const CtaSection = () => {
  return (
    <section
      data-tour="landing-cta"
      className="relative flex w-full flex-col items-center justify-center overflow-visible px-5 pb-10 pt-20 md:pb-20 md:pt-60 lg:pt-60"
    >
      <motion.div
        aria-hidden
        className="absolute inset-0 top-[-90px] z-0"
        variants={glowVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        <CtaGlow />
      </motion.div>

      <motion.div
        className="relative z-10 mx-auto flex max-w-4xl flex-col items-center justify-start gap-9 text-center"
        variants={contentVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        <div className="flex flex-col items-center justify-start gap-4 text-center">
          <motion.h2
            variants={itemVariants}
            className="max-w-full break-words text-4xl font-semibold leading-tight text-foreground md:text-5xl md:leading-tight lg:text-[68px] lg:leading-[76px]"
          >
            {m.landing_cta_title()}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="max-w-2xl break-words text-sm font-medium leading-[18.20px] text-muted-foreground md:text-base md:leading-relaxed"
          >
            {m.landing_cta_subtitle()}
          </motion.p>
        </div>
        <motion.div
          variants={ctaButtonsVariants}
          className="flex flex-col items-center gap-4 sm:inline-flex sm:flex-row sm:justify-center"
        >
          <Link
            to="/contact"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-linear-to-r from-foreground/80 via-foreground to-foreground/80 px-8 py-3 text-base font-medium text-background shadow-lg transition-colors duration-150 ease-in-out hover:bg-foreground sm:w-auto"
          >
            {m.landing_cta_request_demo()}
            <span className="text-primary transition-transform duration-150 ease-in-out group-hover:translate-x-0.5">
              <ArrowRight className="size-4 text-background" />
            </span>
          </Link>
          <span className="inline-flex rounded-full bg-background">
            <Link
              to="/login"
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-8 py-3 text-base font-medium text-foreground shadow-lg backdrop-blur-sm transition-colors duration-150 ease-in-out hover:bg-primary/15 sm:w-auto"
            >
              <LogIn className="size-4" />
              {m.landing_cta_client_sign_in()}
            </Link>
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};

function CtaGlow() {
  return (
    <svg
      className="h-full w-full"
      viewBox="0 0 1388 825"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
    >
      <mask
        id="cta-mask"
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x="269"
        y="27"
        width="850"
        height="493"
      >
        <rect x="269.215" y="27.4062" width="849.57" height="492.311" fill="url(#cta-mask-gradient)" />
      </mask>
      <g mask="url(#cta-mask)">
        <g filter="url(#cta-blur)">
          <ellipse cx="694" cy="-93.0414" rx="670.109" ry="354.908" fill="url(#cta-radial)" fillOpacity="0.8" />
        </g>
        <ellipse cx="694" cy="-91.5385" rx="670.109" ry="354.908" fill="url(#cta-sheen)" />
        <ellipse cx="694" cy="-93.0414" rx="670.109" ry="354.908" fill="url(#cta-fade)" />
      </g>
      <defs>
        <filter
          id="cta-blur"
          x="-234.109"
          y="-705.949"
          width="1856.22"
          height="1225.82"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="129" result="effect1_foregroundBlur" />
        </filter>
        <linearGradient
          id="cta-mask-gradient"
          x1="1118.79"
          y1="273.562"
          x2="269.215"
          y2="273.562"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--background)" stopOpacity="0" />
          <stop offset="0.2" stopColor="var(--background)" stopOpacity="0.8" />
          <stop offset="0.8" stopColor="var(--background)" stopOpacity="0.8" />
          <stop offset="1" stopColor="var(--background)" stopOpacity="0" />
        </linearGradient>
        <radialGradient
          id="cta-radial"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(683.482 245.884) rotate(-3.78676) scale(469.009 248.4)"
        >
          <stop offset="0.1294" stopColor="color-mix(in oklch, var(--primary), black 18%)" />
          <stop offset="0.2347" stopColor="var(--primary)" />
          <stop offset="0.3" stopColor="var(--primary)" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cta-sheen" x1="694" y1="-446.446" x2="694" y2="263.369" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" stopOpacity="0" />
          <stop offset="1" stopColor="white" stopOpacity="0.1" />
        </linearGradient>
        <linearGradient id="cta-fade" x1="694" y1="-447.949" x2="694" y2="261.866" gradientUnits="userSpaceOnUse">
          <stop stopColor="var(--page-background)" />
          <stop offset="1" stopColor="var(--page-background)" />{" "}
        </linearGradient>
      </defs>
    </svg>
  );
}
